import { useState, useEffect } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Star, MapPin, CheckCircle, PlusCircle, CircleNotch, GoogleLogo } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_REVIEW_URL = "https://search.google.com/local/writereview?placeid=ChIJx8Eov0VYD6wRPjfrK_i3LIE";

const SERVICES = [
  "Furniture Removal",
  "Appliance Removal",
  "Yard Waste & Debris",
  "Construction Debris",
  "Dump Truck For Hire With Driver",
  "Estate / Foreclosure Cleanout",
  "Hot Tub Removal",
  "Garage Cleanout",
  "Other",
];

const PALETTE = ["#15306E", "#46A01E", "#2D6CC6", "#7C3AED", "#EA580C", "#0E7490", "#BE185D"];

const initials = (name) =>
  name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2 text-[#46A01E] font-display font-bold uppercase tracking-widest text-xs mb-4">
    <span className="h-px w-8 bg-[#46A01E]" />
    {children}
  </div>
);

function ReviewCard({ rv }) {
  return (
    <div data-testid={`review-${rv.name}`} className="border border-[#E4E4E7] p-7 rounded-sm flex flex-col hover:border-[#46A01E] hover:-translate-y-1 transition-all">
      <div className="flex items-center gap-3 mb-4">
        <span className="flex items-center justify-center h-11 w-11 rounded-full text-white font-display font-extrabold text-sm shrink-0" style={{ backgroundColor: rv.color }}>
          {initials(rv.name)}
        </span>
        <div className="min-w-0">
          <p className="font-display font-extrabold text-[#15306E] leading-tight">{rv.name}</p>
          <p className="text-xs text-[#52525B] flex items-center gap-1">
            <MapPin size={12} weight="fill" /> {rv.location || "San Diego County"}{rv.time ? ` · ${rv.time}` : ""}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 mb-3">
        <span className="flex gap-0.5 text-[#5DB82F]">
          {[...Array(5)].map((_, i) => (
            <Star key={i} size={16} weight="fill" className={i < rv.rating ? "" : "text-[#D4D4D8]"} />
          ))}
        </span>
        <span className="inline-flex items-center gap-1 text-[#46A01E] text-xs font-semibold">
          <CheckCircle size={13} weight="fill" /> Verified
        </span>
      </div>
      <p className="text-[#0A0A0A] leading-relaxed flex-1 text-[15px]">"{rv.text}"</p>
      {rv.service && (
        <div className="mt-5 pt-4 border-t border-[#E4E4E7]">
          <span className="inline-block bg-[#EAF4E3] text-[#15306E] text-xs font-semibold px-2.5 py-1 rounded-sm">{rv.service}</span>
        </div>
      )}
    </div>
  );
}

export default function ReviewsSection() {
  const [fetched, setFetched] = useState([]);
  const [agg, setAgg] = useState({ rating_value: 5, review_count: 0 });
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", location: "", rating: 5, text: "", service: "" });

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then(({ data }) =>
        setFetched(
          data.map((r, i) => ({ ...r, color: PALETTE[i % PALETTE.length], time: "recently" }))
        )
      )
      .catch(() => {});
    axios
      .get(`${API}/reviews/aggregate`)
      .then(({ data }) => data && setAgg(data))
      .catch(() => {});
  }, []);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) {
      toast.error("Please add your name and a few words about your experience.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.location) delete payload.location;
      if (!payload.service) delete payload.service;
      await axios.post(`${API}/reviews`, payload);
      toast.success("Thank you! Your review was submitted and will appear after a quick review. ⭐");
      setForm({ name: "", location: "", rating: 5, text: "", service: "" });
      setOpen(false);
    } catch (err) {
      toast.error("Couldn't submit your review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const all = fetched;

  const ratingSchema = {
    "@context": "https://schema.org",
    "@type": "MovingCompany",
    "@id": "https://cleanout247.com/#business",
    name: "CleanOut247",
    url: "https://cleanout247.com/",
    ...(agg.review_count > 0 && { aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: String(agg.rating_value),
      reviewCount: String(agg.review_count),
      bestRating: "5",
      worstRating: "1",
    } }),
    review: all.slice(0, 12).map((rv) => ({
      "@type": "Review",
      author: { "@type": "Person", name: rv.name },
      reviewRating: { "@type": "Rating", ratingValue: String(rv.rating), bestRating: "5" },
      reviewBody: rv.text,
    })),
  };

  return (
    <section id="reviews" className="px-5 lg:px-8 py-20 lg:py-28">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(ratingSchema)}</script>
      </Helmet>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <Eyebrow>Customer Reviews</Eyebrow>
            <h2 className="font-display font-black tracking-tighter text-4xl lg:text-5xl text-[#15306E]">
              Neighbors who got their space back.
            </h2>
            {agg.review_count > 0 && <div data-testid="reviews-aggregate" className="flex items-center gap-2.5 mt-4">
              <span className="flex gap-0.5 text-[#5DB82F]">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={20} weight="fill" className={i < Math.round(agg.rating_value) ? "" : "text-[#D4D4D8]"} />
                ))}
              </span>
              <span className="font-display font-extrabold text-[#15306E] text-lg">{agg.rating_value.toFixed(1)}</span>
              <span className="text-[#52525B] text-sm">from {agg.review_count} happy customers</span>
            </div>}
          </div>

          <div className="flex flex-wrap gap-3 shrink-0">
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="leave-google-review-button"
              className="flex items-center gap-2 border border-[#E4E4E7] hover:border-[#15306E] text-[#15306E] font-display font-extrabold uppercase tracking-wide text-sm px-6 py-3.5 rounded-sm transition-colors"
            >
              <GoogleLogo size={20} weight="bold" /> Review us on Google
            </a>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button
                  data-testid="open-review-form-button"
                  className="flex items-center gap-2 bg-[#15306E] hover:bg-[#46A01E] text-white font-display font-extrabold uppercase tracking-wide text-sm px-6 py-3.5 rounded-sm transition-colors"
                >
                  <PlusCircle size={20} weight="fill" /> Leave a Review
                </button>
              </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display font-extrabold text-2xl tracking-tight text-[#15306E]">
                  Share your experience
                </DialogTitle>
                <DialogDescription className="text-sm text-[#52525B]">
                  Tell us how Matt and CleanOut247 did. Reviews appear after a quick check.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} data-testid="review-form" className="space-y-3 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <input
                    data-testid="review-name-input"
                    aria-label="Your name"
                    className="w-full border border-[#E4E4E7] px-4 py-3 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#46A01E]"
                    placeholder="Your name *"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                  />
                  <input
                    data-testid="review-location-input"
                    aria-label="City (optional)"
                    className="w-full border border-[#E4E4E7] px-4 py-3 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#46A01E]"
                    placeholder="City (optional)"
                    value={form.location}
                    onChange={(e) => update("location", e.target.value)}
                  />
                </div>

                <div>
                  <p className="text-sm font-semibold text-[#15306E] mb-1.5">Your rating</p>
                  <div className="flex gap-1" data-testid="review-rating">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        data-testid={`review-star-${n}`}
                        aria-label={`Rate ${n} star${n > 1 ? "s" : ""}`}
                        aria-pressed={n <= form.rating}
                        onClick={() => update("rating", n)}
                        className="transition-transform hover:scale-110"
                      >
                        <Star size={30} weight="fill" className={n <= form.rating ? "text-[#5DB82F]" : "text-[#D4D4D8]"} />
                      </button>
                    ))}
                  </div>
                </div>

                <Select value={form.service} onValueChange={(v) => update("service", v)}>
                  <SelectTrigger
                    data-testid="review-service-select"
                    aria-label="Service used (optional)"
                    className="w-full border border-[#E4E4E7] px-4 py-3 h-auto text-sm rounded-sm data-[placeholder]:text-[#A1A1AA] focus:ring-2 focus:ring-[#46A01E]"
                  >
                    <SelectValue placeholder="Service used (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`review-service-option-${s}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <textarea
                  data-testid="review-text-input"
                  aria-label="Tell us how it went"
                  rows={4}
                  className="w-full border border-[#E4E4E7] px-4 py-3 text-sm rounded-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#46A01E]"
                  placeholder="Tell us how it went... *"
                  value={form.text}
                  onChange={(e) => update("text", e.target.value)}
                />

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-review-button"
                  className="w-full bg-[#46A01E] hover:bg-[#3A8418] text-white font-display font-extrabold uppercase tracking-wide text-sm py-3.5 rounded-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {submitting ? <CircleNotch size={20} weight="bold" className="animate-spin" /> : "Submit Review"}
                </button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {all.length === 0 && (
          <p data-testid="reviews-empty" className="text-[#52525B]">Be the first to share how your cleanout went.</p>
        )}
        <div className="grid md:grid-cols-3 gap-5">
          {all.map((rv, i) => (
            <ReviewCard key={`${rv.name}-${i}`} rv={rv} />
          ))}
        </div>
      </div>
    </section>
  );
}
